import { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Divider, Switch, Typography } from "@mui/joy";
import { ArrowLeft, Shield, Trash2 } from "lucide-react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Alert, Button, ControlledTextField, TextField } from "../../../../shared";
import { usePlatformAdmin } from "../../../../hooks/query";
import { useApiResponseParser, useAuth, useAuthorization } from "../../../../hooks";
import { ROLES } from "../../../../constants/permissions";
import Breadcrumb from "../List/Breadcrumb";

import type { IPlatformAdminOrganization } from "../../../../interfaces";

const editSchema = z.object({
    FirstName: z.string().min(1, "First name is required"),
    LastName: z.string().min(1, "Last name is required"),
    IsActive: z.boolean(),
});

function isTruthy(value: boolean | number | string | null | undefined) {
    return value === true || value === 1 || value === "1";
}

function MembershipRow({ membership }: { membership: IPlatformAdminOrganization }) {
    const isImpersonation = isTruthy(membership.IsImpersonation);
    const membershipActive = isTruthy(membership.MembershipIsActive);
    const orgActive = isTruthy(membership.OrganizationIsActive);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
                py: 1.25,
                px: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": { borderBottom: "none" },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography level="title-sm" noWrap>
                    {membership.OrganizationName}
                </Typography>
                <Typography level="body-xs" color="neutral">
                    {[membership.ShortName, membership.RoleName].filter(Boolean).join(" · ") ||
                        "No org role assigned"}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    gap: 0.5,
                    flexShrink: 0,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                }}
            >
                {isImpersonation ? (
                    <Chip size="sm" variant="soft" color="warning" sx={{ borderRadius: "4px" }}>
                        Impersonation
                    </Chip>
                ) : (
                    <Chip size="sm" variant="soft" color="primary" sx={{ borderRadius: "4px" }}>
                        Member
                    </Chip>
                )}
                <Chip
                    size="sm"
                    variant="soft"
                    color={membershipActive && orgActive ? "success" : "neutral"}
                    sx={{ borderRadius: "4px" }}
                >
                    {membershipActive && orgActive ? "Active" : "Inactive"}
                </Chip>
            </Box>
        </Box>
    );
}

export default function EditAdmin() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { parseErrorResponseData } = useApiResponseParser();
    const { hasRole } = useAuthorization();
    const { user } = useAuth();
    const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN);

    const { useDescribe, useUpdate, useRevoke } = usePlatformAdmin();
    const { data: admin, isLoading, isError, error } = useDescribe(id);
    const updateSvc = useUpdate();
    const revokeSvc = useRevoke();

    const [localError, setLocalError] = useState<string | null>(null);
    const [confirmRevoke, setConfirmRevoke] = useState("");

    const methods = useForm({
        mode: "onChange",
        resolver: zodResolver(editSchema),
        defaultValues: {
            FirstName: "",
            LastName: "",
            IsActive: true,
        },
    });

    const {
        handleSubmit,
        reset,
        control,
        formState: { isValid, isDirty },
    } = methods;

    useEffect(() => {
        if (!admin) return;
        reset({
            FirstName: admin.FirstName || "",
            LastName: admin.LastName || "",
            IsActive: admin.IsActive === true || admin.IsActive === 1,
        });
    }, [admin, reset]);

    const isSelf = !!user?.Id && user.Id === id;
    const canRevoke = !isSelf && confirmRevoke.trim().toLowerCase() === "revoke";
    const organizations = admin?.Organizations || [];
    const organizationCount = Number(admin?.OrganizationCount ?? 0);

    const onSave = async (data: { FirstName: string; LastName: string; IsActive: boolean }) => {
        if (!id) return;
        setLocalError(null);
        try {
            await updateSvc.mutateAsync({
                Id: id,
                FirstName: data.FirstName,
                LastName: data.LastName,
                IsActive: data.IsActive,
            });
            navigate("/admin/tenant/admins");
        } catch (err) {
            setLocalError(parseErrorResponseData(err) || "Failed to update admin.");
        }
    };

    const onRevoke = async () => {
        if (!id || !canRevoke) return;
        setLocalError(null);
        try {
            await revokeSvc.mutateAsync({ Id: id });
            navigate("/admin/tenant/admins");
        } catch (err) {
            setLocalError(parseErrorResponseData(err) || "Failed to revoke admin access.");
        }
    };

    if (!isSuperAdmin) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Only Super Admin can manage platform admins." />
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !admin) {
        return (
            <Box flex={1} p={3}>
                <Alert
                    errorMessage={
                        parseErrorResponseData(error) ||
                        (error instanceof Error ? error.message : "Admin not found")
                    }
                />
            </Box>
        );
    }

    const isSaving = updateSvc.isPending || revokeSvc.isPending;

    return (
        <Box flex={1}>
            <Breadcrumb />
            <Divider />

            <Box p={3} pb={0} maxWidth={700}>
                <Typography level="title-lg">Edit Platform Admin</Typography>
                <Typography level="body-sm">
                    Update profile details or revoke Super Admin access.
                </Typography>
            </Box>

            <Box ml={3} mt={2} maxWidth={700} pb={4}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSave)}>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Box>
                                <Typography
                                    level="body-xs"
                                    sx={{ fontWeight: 600, color: "text.secondary" }}
                                >
                                    Email
                                </Typography>
                                <Typography level="body-md" sx={{ mt: 0.25 }}>
                                    {admin.Email}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 1.5,
                                }}
                            >
                                <TextField name="FirstName" label="First Name" placeholder="Jane" />
                                <TextField name="LastName" label="Last Name" placeholder="Doe" />
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    py: 0.5,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                        Active
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                        {isSelf
                                            ? "You cannot deactivate your own account."
                                            : "Inactive admins retain the role but are marked unavailable in the list."}
                                    </Typography>
                                </Box>
                                <Controller
                                    name="IsActive"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Switch
                                            checked={!!value}
                                            disabled={isSelf}
                                            onChange={(event) => onChange(event.target.checked)}
                                            sx={{
                                                flexShrink: 0,
                                                "--Switch-thumbSize": "16px",
                                                "--Switch-trackWidth": "36px",
                                                "--Switch-trackHeight": "20px",
                                            }}
                                        />
                                    )}
                                />
                            </Box>

                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <Typography level="title-sm">Organization memberships</Typography>
                                    <Chip size="sm" variant="soft" color="neutral" sx={{ borderRadius: "4px" }}>
                                        {organizationCount === 1
                                            ? "1 active"
                                            : `${organizationCount} active`}
                                    </Chip>
                                </Box>
                                <Box
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: "4px",
                                        bgcolor: "background.surface",
                                        overflow: "hidden",
                                    }}
                                >
                                    {organizations.length === 0 ? (
                                        <Typography level="body-sm" color="neutral" sx={{ p: 1.5 }}>
                                            No organization memberships yet.
                                        </Typography>
                                    ) : (
                                        organizations.map((membership) => (
                                            <MembershipRow
                                                key={`${membership.OrganizationId}-${membership.IsImpersonation}`}
                                                membership={membership}
                                            />
                                        ))
                                    )}
                                </Box>
                            </Box>

                            <Box mt={1} display="flex" alignItems="center" gap={1.5}>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="neutral"
                                    startDecorator={<ArrowLeft size={18} />}
                                    onClick={() => navigate("/admin/tenant/admins")}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="soft"
                                    color="primary"
                                    startDecorator={<Shield size={18} />}
                                    loading={updateSvc.isPending}
                                    disabled={!isValid || !isDirty || isSaving}
                                >
                                    Save changes
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </FormProvider>

                <Box
                    mt={3}
                    sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "danger.outlinedBorder",
                        borderRadius: "4px",
                        bgcolor: "rgba(var(--standard-palette-danger-mainChannel) / 0.04)",
                    }}
                >
                    <Typography level="title-sm" sx={{ color: "danger.plainColor" }}>
                        Danger zone
                    </Typography>
                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5, mb: 1.5 }}>
                        {isSelf
                            ? "You cannot revoke your own platform admin access."
                            : 'Removes Super Admin from this user. Type "revoke" to confirm. Their Cognito account and any other roles stay intact.'}
                    </Typography>

                    {!isSelf && (
                        <Box display="flex" flexDirection="column" gap={1.25} maxWidth={320}>
                            <ControlledTextField
                                label='Type "revoke" to confirm'
                                value={confirmRevoke}
                                placeholder="revoke"
                                onChange={setConfirmRevoke}
                            />
                            <Button
                                type="button"
                                variant="solid"
                                color="danger"
                                startDecorator={<Trash2 size={16} />}
                                loading={revokeSvc.isPending}
                                disabled={!canRevoke || isSaving}
                                onClick={() => void onRevoke()}
                            >
                                Revoke Super Admin
                            </Button>
                        </Box>
                    )}
                </Box>

                {(localError || updateSvc.isError || revokeSvc.isError) && (
                    <Box mt={2}>
                        <Alert
                            errorMessage={
                                localError ||
                                parseErrorResponseData(updateSvc.error || revokeSvc.error)
                            }
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
}
